import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/lib/toast';
import { normalizeEbayFunctionError } from '@/lib/ebayFunctionErrors';

export interface EbayListingRow {
	id: string;
	user_id: string;
	item_id: string;
	offer_id: string | null;
	listing_id: string | null;
	status: 'pending' | 'active' | 'ended' | 'error';
	last_error: string | null;
	listed_at: string | null;
	format: 'fixed_price' | 'auction' | null;
	end_time: string | null;
	last_refreshed_at: string | null;
	price_cents: number | null;
}

export function useEbayListing(itemId: string | undefined) {
	const { user } = useAuth();
	const queryClient = useQueryClient();

	const query = useQuery({
		queryKey: ['ebay-listing', itemId, user?.id],
		enabled: !!itemId && !!user?.id,
		queryFn: async (): Promise<EbayListingRow | null> => {
			const { data, error } = await (supabase as any)
				.from('ebay_listings')
				.select('*')
				.eq('item_id', itemId)
				.maybeSingle();
			if (error) throw error;
			return data;
		}
	});

	const list = useMutation({
		mutationFn: async (opts?: { format?: 'fixed_price' | 'auction' }) => {
			if (!itemId) throw new Error('Missing item_id');
			const fn = opts?.format === 'auction' ? 'ebay-list-auction' : 'ebay-list-item';
			const throwNormalized = (body: unknown, fallback: string) => {
				const normalized = normalizeEbayFunctionError(body, fallback);
				const e = new Error(normalized.message);
				if (normalized.code) (e as any).code = normalized.code;
				if (Array.isArray(normalized.missing)) (e as any).missing = normalized.missing;
				if (normalized.categoryId) (e as any).category_id = normalized.categoryId;
				throw e;
			};
			const { data, error } = await supabase.functions.invoke(fn, {
				body: { item_id: itemId }
			});
			if (error) {
				const response = (error as any)?.context;
				if (response instanceof Response) {
					const body = await response.json().catch(() => null);
					throwNormalized(body, error.message);
				}
				throw error;
			}
			if ((data as any)?.error) throwNormalized(data, 'eBay listing failed');
			return data as { ok: boolean; listing_id: string; view_url: string | null; end_time?: string };
		},
		onSuccess: (data, vars) => {
			toast.success(vars?.format === 'auction' ? 'Auction started on eBay' : 'Listed on eBay', {
				action: data?.view_url
					? { label: 'View', onClick: () => window.open(data.view_url!, '_blank') }
					: undefined
			});
			queryClient.invalidateQueries({ queryKey: ['ebay-listing', itemId] });
			queryClient.invalidateQueries({ queryKey: ['my-active-ebay-item-ids'] });
		},
		onError: (err: any) => {
			const msg = String(err?.message ?? err);
			if (err?.code === 'aspects_missing' || Array.isArray(err?.missing)) {
				toast.warning('eBay needs a few more details — see the highlighted fields.');
			} else if (msg.includes('reconnect_required')) {
				toast.error('Reconnect eBay — we added permissions to auto-set up your store.');
			} else if (msg.includes('setup_incomplete')) {
				toast.error('eBay setup is still incomplete. Open Profile → eBay connection to retry.');
			} else if (msg.includes('price_required')) {
				toast.error('Set an asking price before listing on eBay.');
			} else if (msg.includes('auction_start_price_required')) {
				toast.error('Set an auction start price first.');
			} else if (msg.includes('postal_code_required')) {
				toast.error('Add a postal code in Profile → Location before starting an auction.');
			} else if (msg.includes('photo_required')) {
				toast.error('Auctions need at least one real photo.');
			} else if (msg.includes('bin_below_start')) {
				toast.error('Buy It Now price must be at or above the start price.');
			} else {
				toast.error(`eBay listing failed: ${msg}`);
			}
		}
	});

	const end = useMutation({
		mutationFn: async () => {
			if (!itemId) throw new Error('Missing item_id');
			const { data, error } = await supabase.functions.invoke('ebay-end-listing', {
				body: { item_id: itemId }
			});
			if (error) {
				const response = (error as any)?.context;
				if (response instanceof Response) {
					const body = await response.json().catch(() => null);
					throw new Error(body?.message || body?.error || error.message);
				}
				throw error;
			}
			if ((data as any)?.error) throw new Error((data as any).error);
			return data;
		},
		onSuccess: () => {
			toast.success('Listing ended on eBay');
			queryClient.invalidateQueries({ queryKey: ['ebay-listing', itemId] });
			queryClient.invalidateQueries({ queryKey: ['my-active-ebay-item-ids'] });
		},
		onError: (err: any) => {
			toast.error(`Could not end listing: ${err?.message ?? err}`);
		}
	});

	const refresh = useMutation({
		mutationFn: async () => {
			if (!itemId) throw new Error('Missing item_id');
			const { data, error } = await supabase.functions.invoke('ebay-refresh-listing', {
				body: { item_id: itemId }
			});
			if (error) {
				const response = (error as any)?.context;
				if (response instanceof Response) {
					const body = await response.json().catch(() => null);
					const e = new Error(body?.message || body?.error || error.message);
					(e as any).code = body?.error;
					throw e;
				}
				throw error;
			}
			if ((data as any)?.error) throw new Error((data as any).error);
			return data as { ok: boolean; listing_id: string | null; view_url: string | null; last_refreshed_at: string };
		},
		onSuccess: (data) => {
			toast.success('Refreshed on eBay', {
				description: 'Listing bumped — keeps watchers and history.',
				action: data?.view_url
					? { label: 'View', onClick: () => window.open(data.view_url!, '_blank') }
					: undefined
			});
			queryClient.invalidateQueries({ queryKey: ['ebay-listing', itemId] });
			queryClient.invalidateQueries({ queryKey: ['my-active-ebay-item-ids'] });
			queryClient.invalidateQueries({ queryKey: ['my-ebay-listings'] });
		},
		onError: (err: any) => {
			const code = err?.code;
			const msg = String(err?.message ?? err);
			if (code === 'rate_limited') {
				toast.error(msg || 'Refreshed too recently. Try again later.');
			} else if (code === 'auction_not_supported') {
				toast.error('Auctions can\'t be soft-refreshed.');
			} else if (code === 'no_active_listing') {
				toast.error('No active eBay listing to refresh.');
			} else {
				toast.error(`Refresh failed: ${msg}`);
			}
		}
	});

	return { ...query, list, end, refresh };
}

export function useEbayPolicies() {
	const { user } = useAuth();
	const queryClient = useQueryClient();

	const sync = useMutation({
		mutationFn: async () => {
			const { data, error } = await supabase.functions.invoke('ebay-policies');
			if (error) throw error;
			return data as {
				ready: boolean;
				missing: string[];
				fulfillment_policy_id: string | null;
				payment_policy_id: string | null;
				return_policy_id: string | null;
				merchant_location_key: string | null;
			};
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['ebay-connection', user?.id] });
		},
		onError: (err: any) => {
			toast.error(`Could not sync eBay setup: ${err.message ?? err}`);
		}
	});

	return { sync };
}
